// Client-side image processing (spec §21/§24): resize, compress, and
// convert to WebP in the browser before upload, so the server spends CPU
// and bandwidth only on an already-small file. This is a bandwidth/UX
// optimization only — the server (see server/src/modules/media/
// imagePipeline.js) re-validates and re-encodes everything itself and
// never trusts what the client sends.
const MAX_DIMENSION = 1600;
const OUTPUT_QUALITY = 0.82;
// Sanity cap before we even attempt to decode client-side — a very large
// file could otherwise stall a low-memory mobile browser during decode.
const MAX_INPUT_BYTES = 30 * 1024 * 1024;

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

export async function compressImageForUpload(file) {
  if (!file.type.startsWith('image/')) {
    throw new Error('Please choose an image file.');
  }
  if (file.size > MAX_INPUT_BYTES) {
    throw new Error('That image is too large. Please choose a smaller file.');
  }

  // `imageOrientation: 'from-image'` auto-applies the file's EXIF
  // orientation before we ever draw it, so a photo taken sideways on a
  // phone comes out right-side-up in the compressed output.
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);

    const webp = await canvasToBlob(canvas, 'image/webp', OUTPUT_QUALITY);
    if (webp) return webp;

    // Fallback for the rare browser that can't encode WebP — the server
    // converts to WebP anyway, so this only affects upload size, not the
    // final stored format.
    return await canvasToBlob(canvas, 'image/jpeg', OUTPUT_QUALITY);
  } finally {
    bitmap.close();
  }
}
